import { z } from 'zod';

/**
 * @aiPurpose minimal projection of a `RoadOption` row carried on the
 *   `Request` read shape (#996). The denormalised fields let the client
 *   render selected chips — including historical inactive selections —
 *   without a second round-trip to `/road-options`.
 * @aiInvariant Strict object — a stale client cannot silently accept new
 *   fields. `active` round-trips so the UI can mute historical
 *   selections that were archived after the request persisted.
 */
export const RequestRoadOptionProjectionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    active: z.boolean(),
    sortOrder: z.number().int(),
  })
  .strict();
export type RequestRoadOptionProjection = z.infer<typeof RequestRoadOptionProjectionSchema>;

/**
 * @aiPurpose minimal projection of a `PackagingOption` row carried on
 *   the `Request` read shape (#996). Same shape and rationale as the
 *   road projection above.
 */
export const RequestPackagingOptionProjectionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    active: z.boolean(),
    sortOrder: z.number().int(),
  })
  .strict();
export type RequestPackagingOptionProjection = z.infer<
  typeof RequestPackagingOptionProjectionSchema
>;

/**
/**
 * @aiPurpose write-side shape for the road/packaging multiselects.
 *   Issue #996 explicitly disallows a hard cardinality cap in v1.
 *   Pathological payloads are protected at the HTTP body-size layer,
 *   NOT here. Do not reintroduce a `.max(N)` business cap without
 *   updating the issue/contract.
 */

/**
 * @aiPurpose write-side shape for the road/packaging multiselects.
 *   The wire accepts a flat array of UUIDs — server-side validation
 *   resolves each id to a row and enforces existence + activeness
 *   (with the historical-tolerance rule on PATCH; see
 *   `apps/backend/src/modules/requests/dictionary-selections.helpers.ts`).
 * @aiInvariant the array MUST NOT contain duplicate UUIDs — the
 *   composite PK on the join tables rejects dupes at the DB layer, but
 *   we surface a typed wire-layer validation error first so the client
 *   gets a deterministic 400 instead of a 500 wrapper around a P2002.
 */
const dictionaryIdsArray = z.array(z.string().uuid()).superRefine((ids, ctx) => {
  const seen = new Set<string>();
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i]!;
    if (seen.has(id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'duplicate dictionary id',
        path: [i],
      });
    }
    seen.add(id);
  }
});

export const RequestRoadOptionIdsInputSchema = dictionaryIdsArray;
export const RequestPackagingOptionIdsInputSchema = dictionaryIdsArray;
